import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Data, Router, RouterStateSnapshot } from '@angular/router';
import * as _ from 'lodash';

import { PrivaToggleRouter } from '@priva/utilities/feature-toggles';

import { FeatureToggleGuardData } from './feature-toggle-guard-data.interface';

/**
 * Guard that either allows or denies activation of a route based on feature enabledness,
 * and executes an optional redirect.
 * Requires that a route provides the necessary data to perform these tasks, as an child of
 * its data property (see  {@link Route#data}).
 * If no feature names are provided then route activation will be blocked by default.
 * The guard data object should conform to the {@link FeatureToggleGuardData} interface and
 * be provided via the route data object using this structure:
 *
 *   { toggles: { release: { instance_of_FeatureToggleGuardData } } }
 *
 * The static routeData() function is provided to facilitate proper data object creation.
 */
@Injectable()
export class FeatureToggleGuard {
    /**
     * Helper function to construct a Data object with the appropriate structure for this guard
     * to operate on, which prevents having to expose this knowlegde to consumers.
     *
     * @param featureToggleGuardData A FeatureToggleGuardData object.
     * @param existingData An optional Data object that will be expanded (modified).
     */
    static routeData(featureToggleGuardData: FeatureToggleGuardData, existingData?: Data): Data {
        return existingData
            ? _.merge(existingData, FeatureToggleGuard.dataObject(featureToggleGuardData))
            : FeatureToggleGuard.dataObject(featureToggleGuardData);
    }

    /**
     * Formerly const in routeData, which broke AOT static analysis, resulting in an error
     * during compilation: 'Function calls are not supported in decorators'
     */
    private static dataObject(featureToggleGuardData: FeatureToggleGuardData): Data {
        return { toggles: { release: featureToggleGuardData } };
    }

    constructor(
        private readonly toggleService: PrivaToggleRouter,
        private readonly router: Router,
    ) {}

    canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean {
        return this.isActivationAllowed(route);
    }

    canActivateChild(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean {
        return this.isActivationAllowed(route);
    }

    private isActivationAllowed(route: ActivatedRouteSnapshot): boolean {
        let allowed = false;
        const guardData = this.getGuardData(route);
        const requireAll = Boolean(guardData.requireAll);
        if (guardData.features) {
            const featureEnabledness = this.getFeatureEnabledness(guardData);
            allowed =
                this.isAnyFeatureAllowedAndEnabled(requireAll, featureEnabledness) ||
                this.isEveryFeatureRequiredAndEnabled(requireAll, featureEnabledness);
        }
        if (!allowed && guardData.redirectUrl) {
            this.router.navigateByUrl(guardData.redirectUrl);
        }
        return allowed;
    }

    private getGuardData(route: ActivatedRouteSnapshot): FeatureToggleGuardData {
        return _.get(route, 'data.toggles.release') || {};
    }

    private getFeatureEnabledness(guardData: FeatureToggleGuardData): boolean[] {
        return guardData.features.map((feature) => this.toggleService.isEnabled(feature));
    }

    private isAnyFeatureAllowedAndEnabled(requireAll: boolean, featureEnabledness: boolean[]): boolean {
        return !requireAll && featureEnabledness.find(Boolean);
    }

    private isEveryFeatureRequiredAndEnabled(requireAll: boolean, featureEnabledness: boolean[]): boolean {
        return requireAll && featureEnabledness.every(Boolean);
    }
}
